import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Image } from 'Components/Image';
import Spacer from 'Components/Spacer';
import { Typography } from 'Components/Typography';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${theme.colors.neutral[300]};
  padding: 63px 34px 85px 34px;

  ${legacyTheme.breakpoints.up('md')} {
    flex-direction: row;
    padding: 80px;
  }
`;

const ListItem = styled.div`
  display: flex;
  flex-direction: ${({ isIconOnSide }) => (isIconOnSide ? 'row' : 'column')};
`;

const IconContainer = styled.div`
  margin-right: 88px;
  margin-bottom: 20px;
  margin-left: -34px;
  ${legacyTheme.breakpoints.match('md')} {
    margin-right: 0px;
  }
`;

const ColouredTypography = styled(Typography)`
  color: ${theme.colors.primary['400']};
  font-size: 16px;
`;

const Divider = styled.div`
  width: 100%;
  background-color: black;
  height: 0.3px;
  margin: 45px 0px;

  ${legacyTheme.breakpoints.up('md')} {
    width: 0.3px;
    height: ${({ height }) => height - 160}px;
    margin: 0px 45px;
  }
`;

const SeparatorDiv = styled.div``;

const InformationItem = ({ title, alt, icon, description, isIconOnSide }) => {
  const Separator = isIconOnSide ? SeparatorDiv : React.Fragment;
  return (
    <ListItem isIconOnSide={isIconOnSide}>
      <IconContainer>
        <Image alt={alt} src={icon} width={140} />
      </IconContainer>
      <Separator>
        <ColouredTypography bold markupName="h4" variant="mono1">
          {title}
        </ColouredTypography>
        <Spacer size={10} />
        <ColouredTypography variant="p1">{description}</ColouredTypography>
      </Separator>
    </ListItem>
  );
};

InformationItem.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  icon: srcOrNextImageProp.isRequired,
  isIconOnSide: PropTypes.bool,
};

InformationItem.defaultProps = {
  isIconOnSide: true,
};

const CertificateBlock = ({ bCorp, climateNeutral }) => {
  const { isMobile } = useResponsiveVariant();
  const [dividerHeight, setDividerHeight] = useState(0);
  const wrapperRef = useRef();

  useEffect(() => {
    if (!dividerHeight) {
      setDividerHeight(wrapperRef?.current?.clientHeight);
    }
  }, [wrapperRef]);

  return (
    <Wrapper ref={wrapperRef}>
      <InformationItem
        alt={bCorp.alt}
        description={bCorp.description}
        icon={bCorp.icon}
        isIconOnSide={!isMobile}
        title={bCorp.title}
      />
      <Divider height={dividerHeight} />
      <InformationItem
        alt={climateNeutral.alt}
        description={climateNeutral.description}
        icon={climateNeutral.icon}
        isIconOnSide={!isMobile}
        title={climateNeutral.title}
      />
    </Wrapper>
  );
};

CertificateBlock.propTypes = {
  bCorp: PropTypes.shape({
    alt: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    icon: srcOrNextImageProp,
  }).isRequired,
  climateNeutral: PropTypes.shape({
    alt: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    icon: srcOrNextImageProp,
  }).isRequired,
};

export default CertificateBlock;
